import type { SpellId } from '@archon3d/core';
import type { AudioBus, Voice } from './audioBus';
import { midi, noise, tone } from './synth';

/** Each spell has its own chime, so the ear knows what was cast before the board shows it. */
export function spellChime(bus: AudioBus, spell: SpellId): void {
  const v = bus.voice(`spell-${spell}`, { gain: 0.55, reverb: 0.5 });
  if (!v) return;
  v.end(CHIMES[spell](v));
}

const CHIMES: Record<SpellId, (v: Voice) => number> = {
  // Four quick notes climbing away, under a rush of air.
  teleport: (v) => {
    let end = 0;
    [76, 80, 83, 88].forEach((note, i) => {
      end = Math.max(
        end,
        tone(v, {
          freq: midi(note),
          seconds: 0.08,
          at: i * 0.07,
          env: { attack: 0.005, release: 0.35 },
          gain: 0.35,
          fm: { ratio: 2, depth: 90 },
        }),
      );
    });
    return Math.max(
      end,
      noise(v, {
        kind: 'pink',
        seconds: 0.25,
        env: { attack: 0.12, release: 0.3 },
        filter: { type: 'bandpass', freq: 800, sweep: { to: 3200 }, q: 1.5 },
        gain: 0.25,
      }),
    );
  },
  // A warm major chord blooming upwards.
  heal: (v) => {
    let end = 0;
    [60, 64, 67, 72, 76].forEach((note, i) => {
      end = Math.max(
        end,
        tone(v, {
          freq: midi(note),
          seconds: 0.5 - i * 0.05,
          at: i * 0.11,
          env: { attack: 0.04, release: 0.8 },
          gain: 0.28,
          vibrato: { hz: 5.5, cents: 6 },
        }),
      );
    });
    return end;
  },
  // The clock runs backwards: a bent tone dips and returns under a row of ticks.
  'shift-time': (v) => {
    let end = tone(v, {
      type: 'triangle',
      freq: 660,
      sweep: { to: 330, seconds: 0.35 },
      seconds: 0.35,
      env: { attack: 0.03, release: 0.05 },
      gain: 0.35,
    });
    end = Math.max(
      end,
      tone(v, {
        type: 'triangle',
        freq: 330,
        sweep: { to: 880, seconds: 0.4 },
        seconds: 0.4,
        at: 0.35,
        env: { attack: 0.01, release: 0.4 },
        gain: 0.35,
      }),
    );
    for (let i = 0; i < 5; i++)
      end = Math.max(
        end,
        tone(v, {
          type: 'square',
          freq: 2200,
          seconds: 0.012,
          at: 0.1 + i * 0.16,
          env: { attack: 0.001, release: 0.03 },
          gain: 0.12,
        }),
      );
    return end;
  },
  // Two notes swap places, gliding past each other.
  exchange: (v) =>
    Math.max(
      tone(v, {
        freq: 440,
        sweep: { to: 1320 },
        seconds: 0.4,
        env: { attack: 0.02, release: 0.35 },
        gain: 0.3,
        fm: { ratio: 1.5, depth: 60 },
      }),
      tone(v, {
        freq: 1320,
        sweep: { to: 440 },
        seconds: 0.4,
        env: { attack: 0.02, release: 0.35 },
        gain: 0.3,
        fm: { ratio: 1.5, depth: 60 },
      }),
    ),
  // A swell out of the dark that breaks into a bell.
  revive: (v) => {
    let end = tone(v, {
      type: 'sawtooth',
      freq: 110,
      seconds: 0.9,
      env: { attack: 0.7, release: 0.5 },
      gain: 0.3,
      filter: { type: 'lowpass', freq: 200, sweep: { to: 2400 }, q: 2 },
    });
    end = Math.max(
      end,
      tone(v, {
        freq: 1568,
        seconds: 0.4,
        at: 0.85,
        env: { attack: 0.005, release: 1.2 },
        gain: 0.35,
        fm: { ratio: 1.41, depth: 700, depthTo: 30 },
      }),
    );
    return end;
  },
  // Chains: inharmonic clangs, twice, over a drone that sinks.
  // Sour and sinking: three notes bending down a semitone each, the sound of luck turning.
  curse: (v) => {
    let end = 0;
    [70, 66, 61].forEach((note, i) => {
      end = Math.max(
        end,
        tone(v, {
          freq: midi(note) * (1 - i * 0.01),
          seconds: 0.18,
          at: i * 0.11,
          env: { attack: 0.01, release: 0.4 },
          gain: 0.3 - i * 0.05,
        }),
      );
    });
    return end;
  },
  // Brittle and high, a struck icicle: a bright partial stack that stops dead.
  freeze: (v) => {
    let end = 0;
    [1, 2.4, 4.1, 6.7].forEach((ratio, i) => {
      end = Math.max(
        end,
        tone(v, {
          freq: 880 * ratio,
          seconds: 0.04,
          at: i * 0.015,
          env: { attack: 0.001, release: 0.25 / (i + 1) },
          gain: 0.26 / (i + 1),
        }),
      );
    });
    return end;
  },
  // The opposite of Imprison, and deliberately its mirror: an open chord opening upwards.
  release: (v) => {
    let end = 0;
    [64, 71, 76].forEach((note, i) => {
      end = Math.max(
        end,
        tone(v, {
          freq: midi(note),
          seconds: 0.22,
          at: i * 0.06,
          env: { attack: 0.006, release: 0.5 },
          gain: 0.3,
        }),
      );
    });
    return end;
  },
  imprison: (v) => {
    let end = 0;
    [1, 2.76, 5.4, 8.93].forEach((ratio, i) => {
      for (const at of [0, 0.17])
        end = Math.max(
          end,
          tone(v, {
            freq: 330 * ratio * (at ? 1.02 : 1),
            seconds: 0.05,
            at,
            env: { attack: 0.002, release: 0.5 / (i + 1) },
            gain: 0.3 / (i + 1),
          }),
        );
    });
    return Math.max(
      end,
      tone(v, {
        type: 'sawtooth',
        freq: 96,
        sweep: { to: 55 },
        seconds: 0.7,
        env: { attack: 0.05, release: 0.5 },
        gain: 0.25,
        filter: { type: 'lowpass', freq: 500, q: 1 },
      }),
    );
  },
  // Something is called up from below: a rumble swells and breaks into a spread of bright notes.
  'summon-elemental': (v) => {
    let end = noise(v, {
      kind: 'pink',
      seconds: 0.55,
      env: { attack: 0.35, release: 0.3 },
      filter: { type: 'lowpass', freq: 120, sweep: { to: 2200 }, q: 1.2 },
      gain: 0.4,
    });
    end = Math.max(
      end,
      tone(v, {
        type: 'sawtooth',
        freq: 65,
        sweep: { to: 130 },
        seconds: 0.55,
        env: { attack: 0.3, release: 0.4 },
        gain: 0.3,
        filter: { type: 'lowpass', freq: 300, sweep: { to: 1400 }, q: 1.5 },
      }),
    );
    [62, 69, 74, 81].forEach((note, i) => {
      end = Math.max(
        end,
        tone(v, {
          freq: midi(note),
          seconds: 0.35,
          at: 0.5 + i * 0.05,
          env: { attack: 0.004, release: 0.9 },
          gain: 0.3,
          fm: { ratio: 3, depth: 140, depthTo: 10 },
        }),
      );
    });
    return end;
  },
};

/** Every spell with a chime, which is every spell: the tests play each (client-audio-engine:quality#12). */
export const CHIMED_SPELLS = Object.keys(CHIMES) as SpellId[];
